import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Spinner from '../components/ui/Spinner';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Alert from '../components/ui/Alert';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import PageHeader from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';

const TrainingDetails = () => {
  const { t } = useTranslation();
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [training, setTraining] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);

  // SUPER_ADMIN: invalidare semnătură (force re-sign) — resetarea completă s-a mutat pe CourseStats
  const [showInvalidateModal, setShowInvalidateModal] = useState(false);
  const [invalidateReason, setInvalidateReason] = useState('');
  const [invalidating, setInvalidating] = useState(false);

  const handleInvalidateSignature = async () => {
    setInvalidating(true);
    try {
      await api.post(`/trainings/${trainingId}/invalidate-employee-signature`, {
        reason: invalidateReason,
      });
      setShowInvalidateModal(false);
      setInvalidateReason('');
      showToast(t('recovery.invalidateSuccess'), 'success');
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || t('toasts.errorGeneric'), 'error');
    } finally {
      setInvalidating(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [trainingId]);

  const fetchData = async () => {
    try {
      const response = await api.get(`/trainings/${trainingId}`);
      setTraining(response.data);

      // Grupează întrebările pe module
      const moduleResults = response.data.course.modules.map((module) => ({
        moduleName: module.title,
        questions: module.questions,
        score: calculateModuleScore(
          module.questions,
          response.data.testResults?.[0]?.answers || {}
        ),
      }));

      setTestResults(moduleResults);
    } catch (err) {
      console.error(err);
      navigate('/my-courses');
    } finally {
      setLoading(false);
    }
  };

  const calculateModuleScore = (questions, userAnswers) => {
    if (questions.length === 0) return 0;

    let correct = 0;
    questions.forEach((q) => {
      const userAnswer = userAnswers[q.id];
      const correctAnswer = q.answers.find((a) => a.isCorrect);
      if (userAnswer === correctAnswer?.id) {
        correct++;
      }
    });

    return Math.round((correct / questions.length) * 100);
  };

  const getUserAnswer = (questionId) => {
    return training?.testResults?.[0]?.answers?.[questionId];
  };

  if (loading) {
    return <Spinner label={t('trainingDetails.loading')} />;
  }

  if (!training || (!training.passed && user?.role !== 'SUPER_ADMIN')) {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert type="warning">
          <div className="text-center w-full">
            <p className="font-medium">{t('trainingDetails.onlyAfterPass')}</p>
            <div className="mt-4">
              <Button variant="primary" size="sm" onClick={() => navigate('/my-courses')}>
                {t('trainingDetails.backToCourses')}
              </Button>
            </div>
          </div>
        </Alert>
      </div>
    );
  }

  const hasTestResults = training.testResults && training.testResults.length > 0;
  const totalQuestions = training.course.modules.reduce((sum, m) => sum + m.questions.length, 0);
  const hadAnyTest = hasTestResults && totalQuestions > 0;
  const duration =
    training.completedAt && training.startedAt
      ? Math.round((new Date(training.completedAt) - new Date(training.startedAt)) / 1000)
      : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => {
            // Întoarce-te la pagina anterioară dacă există în history; fallback la /my-courses
            if (window.history.length > 1) navigate(-1);
            else navigate('/my-courses');
          }}
          className="text-primary-600 dark:text-primary-300 hover:text-primary-800 mb-2 text-sm flex items-center gap-1 transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
            />
          </svg>
          {t('common.back')}
        </button>
        <PageHeader
          title={(() => {
            const dateLabel = training.completedAt
              ? new Date(training.completedAt).toLocaleDateString('ro-RO', {
                  month: 'long',
                  year: 'numeric',
                })
              : t('trainingDetails.inProgress');
            return hadAnyTest
              ? t('trainingDetails.resultsTitle', { date: dateLabel })
              : t('trainingDetails.trainingTitle', { date: dateLabel });
          })()}
          actions={
            user?.role === 'SUPER_ADMIN' && (
              <Button
                variant="outline"
                size="sm"
                className="text-amber-700 border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                onClick={() => setShowInvalidateModal(true)}
                title={t('recovery.invalidateBtnTitle')}
              >
                {t('recovery.invalidateTitle')}
              </Button>
            )
          }
        />
      </div>

      {/* Summary */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('trainingDetails.employee')}
            </h3>
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {training.employee.user.firstName} {training.employee.user.lastName}
            </p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('trainingDetails.course')}
            </h3>
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {training.course.title}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-primary-50 dark:bg-primary-950/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-1">
              {t('trainingDetails.startedAt')}
            </h3>
            <p className="text-sm text-primary-900 dark:text-primary-200">
              {new Date(training.startedAt).toLocaleString('ro-RO', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </p>
          </div>

          <div className="bg-accent-50 dark:bg-accent-950/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-accent-700 dark:text-accent-300 mb-1">
              {t('trainingDetails.finishedAt')}
            </h3>
            <p className="text-sm text-accent-900 dark:text-accent-200">
              {new Date(training.completedAt).toLocaleString('ro-RO', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </p>
          </div>

          <div className="bg-purple-50 dark:bg-slate-800/60 rounded-lg p-4">
            <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">
              {t('trainingDetails.totalTime')}
            </h3>
            <p className="text-sm text-purple-900 dark:text-purple-200">
              {duration < 60
                ? t('trainingDetails.durationSeconds', { seconds: duration })
                : t('trainingDetails.durationMinutes', {
                    minutes: Math.floor(duration / 60),
                    seconds: duration % 60,
                  })}
            </p>
          </div>
        </div>

        {hadAnyTest ? (
          <div className="bg-gradient-to-r from-accent-50 to-emerald-50 dark:from-slate-950 dark:to-slate-900 border-2 border-accent-200 dark:border-accent-700 rounded-lg p-6">
            <h3 className="text-sm font-medium text-accent-700 dark:text-accent-300 mb-2">
              {t('trainingDetails.averageScore')}
            </h3>
            <p className="text-4xl font-bold text-accent-600 dark:text-accent-300">
              {training.score}% / 100
            </p>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-slate-950 dark:to-slate-900 border-2 border-primary-200 dark:border-primary-700 rounded-lg p-6">
            <h3 className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
              {t('trainingDetails.completionRate')}
            </h3>
            <p className="text-4xl font-bold text-primary-600 dark:text-primary-300">
              {training.progress || 100}%
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('trainingDetails.noTestIncluded')}
            </p>
          </div>
        )}
      </Card>

      {/* Module Scores — only if test existed */}
      {hadAnyTest && (
        <Card>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6">
            {t('trainingDetails.quizScores')}
          </h2>

          <div className="space-y-4">
            {testResults
              .filter((m) => m.questions.length > 0)
              .map((module, index) => (
                <div
                  key={index}
                  className="border-l-4 border-accent-500 dark:border-accent-700 pl-4 py-2"
                >
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                    {module.moduleName}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    <span className="text-accent-600 dark:text-accent-300 font-bold">
                      {module.score}% / 100
                    </span>{' '}
                    {t('trainingDetails.minScore')}
                  </p>
                </div>
              ))}
          </div>
        </Card>
      )}

      {/* Status */}
      <Card>
        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {t('trainingDetails.state')}
        </h3>
        <Badge color="green">
          <span className="flex items-center gap-1.5">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            {hadAnyTest ? t('trainingDetails.evalPassed') : t('trainingDetails.trainingCompleted')}
          </span>
        </Badge>
      </Card>

      {/* Questions Review — only if test existed */}
      {hadAnyTest && (
        <Card>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6">
            {t('trainingDetails.reviewQuestions')}
          </h2>

          <div className="space-y-8">
            {training.course.modules
              .filter((m) => m.questions.length > 0)
              .map((module, mIndex) => (
                <div key={module.id}>
                  {module.questions.map((question, qIndex) => {
                    const userAnswerId = getUserAnswer(question.id);
                    const correctAnswer = question.answers.find((a) => a.isCorrect);
                    const isCorrect = userAnswerId === correctAnswer?.id;

                    return (
                      <div key={question.id} className="mb-6">
                        <div className="flex items-start gap-3 mb-4">
                          <div
                            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                              isCorrect ? 'bg-accent-500' : 'bg-danger-500'
                            }`}
                          >
                            {isCorrect ? (
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M4.5 12.75l6 6 9-13.5"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                              {mIndex + 1}.{qIndex + 1}. {module.title}
                            </h4>
                            <p className="text-slate-700 dark:text-slate-300 mb-3">
                              {question.text}
                            </p>

                            <Badge color={isCorrect ? 'green' : 'red'}>
                              {isCorrect
                                ? t('trainingDetails.pointFull')
                                : t('trainingDetails.pointZero')}
                            </Badge>

                            {isCorrect && (
                              <p className="text-accent-700 dark:text-accent-300 font-medium mt-2">
                                {t('trainingDetails.yourAnswerCorrect')}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="ml-11 space-y-2">
                          {question.answers.map((answer) => {
                            const isUserAnswer = answer.id === userAnswerId;
                            const isCorrectAnswer = answer.isCorrect;

                            return (
                              <div
                                key={answer.id}
                                className={`p-3 rounded-lg border-2 ${
                                  isCorrectAnswer
                                    ? 'border-accent-500 dark:border-accent-700 bg-accent-50 dark:bg-accent-950/50'
                                    : isUserAnswer
                                      ? 'border-danger-500 dark:border-danger-700 bg-danger-50 dark:bg-danger-950/50'
                                      : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {isCorrectAnswer && (
                                    <svg
                                      className="w-4 h-4 text-accent-600 dark:text-accent-300 font-bold flex-shrink-0"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2.5}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4.5 12.75l6 6 9-13.5"
                                      />
                                    </svg>
                                  )}
                                  {isUserAnswer && !isCorrectAnswer && (
                                    <svg
                                      className="w-4 h-4 text-danger-600 dark:text-danger-300 font-bold flex-shrink-0"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2.5}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  )}
                                  <span
                                    className={
                                      isCorrectAnswer
                                        ? 'text-accent-900 dark:text-accent-200 font-medium'
                                        : isUserAnswer
                                          ? 'text-danger-900 dark:text-danger-300'
                                          : 'text-slate-700 dark:text-slate-300'
                                    }
                                  >
                                    {answer.text}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
          </div>
        </Card>
      )}

      {user?.role === 'SUPER_ADMIN' && (
        <Modal
          open={showInvalidateModal}
          onClose={() => !invalidating && setShowInvalidateModal(false)}
          closable={!invalidating}
          title={t('recovery.invalidateTitle')}
          size="md"
          footer={
            <div className="flex gap-3 justify-end w-full">
              <Button
                variant="secondary"
                onClick={() => setShowInvalidateModal(false)}
                disabled={invalidating}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleInvalidateSignature}
                loading={invalidating}
                disabled={invalidating}
              >
                {t('recovery.invalidateConfirm')}
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <p>{t('recovery.invalidateDesc')}</p>
            <p className="text-amber-700 dark:text-amber-300 font-medium">
              {t('recovery.invalidateWarn')}
            </p>
            <Input
              label={t('recovery.invalidateReason')}
              value={invalidateReason}
              onChange={(e) => setInvalidateReason(e.target.value)}
              placeholder={t('recovery.invalidateReasonPh')}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TrainingDetails;
